if (!customElements.get('pf-article-carousel')) {
  customElements.define('pf-article-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.track = this.querySelector('[data-article-track]');
      if (!this.track) return;
      this.events = new AbortController();
      const { signal } = this.events;
      this.previous = this.querySelector('[data-article-prev]');
      this.next = this.querySelector('[data-article-next]');
      this.position = this.querySelector('[data-article-position]');
      this.error = this.querySelector('[data-article-error]');
      this.loading = false;
      this.failed = false;
      this.querySelector('[data-article-controls]').hidden = false;
      this.previous.addEventListener('click', () => this.move(-1), { signal });
      this.next.addEventListener('click', () => this.move(1), { signal });
      this.track.addEventListener('scroll', () => this.schedule(), { passive: true, signal });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track) return;
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.move(event.key === 'ArrowLeft' ? -1 : 1);
      }, { signal });
      this.resize = new ResizeObserver(() => this.schedule());
      this.resize.observe(this.track);
      this.update();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }

    schedule() {
      if (!this.frame) this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    }

    update() {
      const bounds = this.track.getBoundingClientRect();
      if (!bounds.width) return;
      const cards = [...this.track.children];
      const visible = cards.map((card, index) => ({ bounds: card.getBoundingClientRect(), index }))
        .filter(card => card.bounds.right > bounds.left + 2 && card.bounds.left < bounds.right - 2);
      const first = visible[0]?.index ?? 0;
      const last = visible.at(-1)?.index ?? first;
      const remaining = this.track.scrollWidth - this.track.clientWidth - this.track.scrollLeft;
      this.previous.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = remaining <= 2 && !this.dataset.next;
      this.position.textContent = `${first + 1}–${last + 1} / ${this.dataset.total}`;
      if (remaining < this.track.clientWidth && this.dataset.next && !this.failed) this.loadMore();
    }

    async move(direction) {
      if (direction > 0 && this.dataset.next && this.track.scrollWidth - this.track.clientWidth - this.track.scrollLeft <= 2) {
        await this.loadMore(true);
      }
      if (!this.isConnected) return;
      const width = this.track.firstElementChild?.getBoundingClientRect().width ?? this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({ left: direction * (width + gap), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }

    async loadMore(retry = false) {
      if (this.loading || !this.dataset.next || (this.failed && !retry)) return;
      const { signal } = this.events;
      this.loading = true;
      this.error.hidden = true;
      this.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(this.dataset.next, location.origin);
        if (url.origin !== location.origin) throw new Error('Invalid carousel URL');
        url.searchParams.set('section_id', 'pf-article-carousel-page');
        const response = await fetch(url, { signal });
        if (!response.ok) throw new Error('Unable to load blog posts');
        const page = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-article-page]');
        if (!page?.children.length) throw new Error('No blog posts returned');
        if (signal.aborted) return;
        const links = new Set([...this.track.querySelectorAll('a')].map(link => link.getAttribute('href')));
        const label = this.track.querySelector('.pf-blog-card__link')?.textContent;
        for (const card of [...page.children]) {
          if (links.has(card.querySelector('a')?.getAttribute('href'))) continue;
          if (label) card.querySelector('.pf-blog-card__link').textContent = label;
          this.track.append(card);
        }
        this.dataset.next = page.dataset.next || '';
        this.failed = false;
      } catch (error) {
        if (!signal.aborted) {
          this.failed = true;
          this.error.hidden = false;
        }
      } finally {
        if (!signal.aborted) {
          this.loading = false;
          this.removeAttribute('aria-busy');
          this.update();
        }
      }
    }
  });
}
